import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ConveyorBeltIcon from '@mui/icons-material/ConveyorBelt';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import GrassOutlinedIcon from '@mui/icons-material/GrassOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import MonitorWeightOutlinedIcon from '@mui/icons-material/MonitorWeightOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import VaccinesOutlinedIcon from '@mui/icons-material/VaccinesOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import { CattleIcon } from '@components/Icons/CattleIcon';
import { CochoPastureIcon } from '@components/Icons/CochoPastureIcon';
import { appRoutes } from '@routes/appRoutes';

/**
 * Organização da sidebar por fluxo de trabalho da fazenda:
 *
 * 1. Visão Geral   — onde o dia começa: painel e relatórios.
 * 2. Rebanho       — o ativo principal: animais, lotes, manejo e avaliações.
 * 3. Operação de Campo — rotina diária no campo: tarefas, pasto, cocho, sanitário.
 * 4. Nutrição e Produção — cadeia alimentar completa: do ingrediente ao estoque.
 * 5. Comercial     — entrada e saída de dinheiro: compra, venda e parceiros.
 * 6. Administração — cadastros estruturais e acesso ao sistema.
 */
export type NavigationSectionKey = 'overview' | 'herd' | 'operation' | 'nutrition' | 'commercial' | 'admin';

export type NavigationRole = 'view' | 'create' | 'del' | 'upgrade';
export type LicenseScope = 'CONFINEMENT' | 'PASTURE' | 'SHARED';
export type LicenseFeature = 'automation';
export type NavigationAccessLevel = 'admin' | 'leader' | 'normal';

export interface NavigationSection {
  key: NavigationSectionKey;
  label: string;
  order: number;
}

export interface NavigationItem {
  key: string;
  label: string;
  route: string;
  icon: React.ElementType;
  section: NavigationSectionKey;
  order: number;
  module?: string;
  role?: NavigationRole;
  accessLevels?: NavigationAccessLevel[];
  farmRequired?: boolean;
  licenseScope: LicenseScope;
  /** Feature adicional exigida na licença global. Ausente = sempre visível. */
  licenseFeature?: LicenseFeature;
  searchKeywords: string[];
  activePrefixes?: string[];
}

export const navigationSections: NavigationSection[] = [
  { key: 'overview', label: 'Visão Geral', order: 1 },
  { key: 'herd', label: 'Rebanho', order: 2 },
  { key: 'operation', label: 'Operação de Campo', order: 3 },
  { key: 'nutrition', label: 'Nutrição e Produção', order: 4 },
  { key: 'commercial', label: 'Comercial', order: 5 },
  { key: 'admin', label: 'Administração', order: 6 },
];

export const navigationItems: NavigationItem[] = [
  // ── Visão Geral ──────────────────────────────────────────────────────────
  {
    key: 'dashboard',
    label: 'Dashboard',
    route: appRoutes.dashboard,
    icon: DashboardOutlinedIcon,
    section: 'overview',
    order: 1,
    licenseScope: 'SHARED',
    searchKeywords: ['inicio', 'visao geral', 'painel'],
  },
  {
    key: 'relatorios',
    label: 'Relatórios',
    route: appRoutes.relatorios,
    icon: InsightsOutlinedIcon,
    section: 'overview',
    order: 2,
    module: 'report',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['analitico', 'operacional', 'indicadores'],
  },

  // ── Rebanho ──────────────────────────────────────────────────────────────
  {
    key: 'animais',
    label: 'Animais',
    route: appRoutes.animais.list,
    icon: CattleIcon,
    section: 'herd',
    order: 1,
    module: 'animal',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['gado', 'brinco', 'cadastro'],
    activePrefixes: [appRoutes.animais.list],
  },
  {
    key: 'lotes',
    label: 'Lotes',
    route: appRoutes.lotes.list,
    icon: LayersOutlinedIcon,
    section: 'herd',
    order: 2,
    module: 'lot',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['grupos', 'curral', 'area'],
    activePrefixes: [appRoutes.lotes.list],
  },
  {
    key: 'areas',
    label: 'Áreas',
    route: appRoutes.areas.list,
    icon: MapOutlinedIcon,
    section: 'herd',
    order: 3,
    module: 'area',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['setores', 'mapa', 'talhoes'],
    activePrefixes: [appRoutes.areas.list],
  },
  {
    key: 'manejo',
    label: 'Manejo',
    route: appRoutes.manejo.list,
    icon: MonitorWeightOutlinedIcon,
    section: 'herd',
    order: 4,
    module: 'handling',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['manejo', 'pesagem', 'apartacao', 'apartação', 'fase', 'transferencia', 'transferência', 'curral'],
    activePrefixes: [appRoutes.manejo.list],
  },
  {
    key: 'avaliacoes',
    label: 'Saídas',
    route: appRoutes.avaliacoes.list,
    icon: QueryStatsOutlinedIcon,
    section: 'herd',
    order: 5,
    module: 'assessment',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['saída', 'saidas', 'saída de animais', 'venda', 'abate', 'pesagem', 'avaliação', 'avaliação de venda', 'avaliações de venda', 'lote de saída', 'lotes de saída'],
    activePrefixes: [appRoutes.avaliacoes.list],
  },
  {
    key: 'movimentacoes',
    label: 'Movimentação',
    route: appRoutes.movimentacoes.list,
    icon: HistoryOutlinedIcon,
    section: 'herd',
    order: 6,
    module: 'lot',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['movimentacao', 'movimentação', 'historico', 'histórico', 'lote', 'curral', 'cancelar'],
    activePrefixes: [appRoutes.movimentacoes.list],
  },

  // ── Operação de Campo ────────────────────────────────────────────────────
  {
    key: 'operacao',
    label: 'Operação',
    route: appRoutes.operacao.list,
    icon: TaskAltOutlinedIcon,
    section: 'operation',
    order: 1,
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['operacao', 'operação', 'tarefas', 'diario', 'rotina'],
    activePrefixes: [appRoutes.operacao.list],
  },
  {
    key: 'pasto',
    label: 'Pasto',
    route: appRoutes.pasto,
    icon: GrassOutlinedIcon,
    section: 'operation',
    order: 2,
    module: 'pasture',
    role: 'view',
    farmRequired: true,
    licenseScope: 'PASTURE',
    searchKeywords: ['pasto', 'piquete', 'manejo', 'rotacionado', 'continuo', 'rotacao', 'descanso', 'capim', 'ocupacao'],
  },
  {
    key: 'cocho',
    label: 'Cocho',
    route: appRoutes.cocho.list,
    icon: CochoPastureIcon,
    section: 'operation',
    order: 3,
    module: 'nutrition',
    role: 'view',
    farmRequired: true,
    // Cocho é exclusivo de confinamento (decisão de produto, auditoria §5.5)
    // — mesmo escopo do backend (CochoController = CONFINEMENT).
    licenseScope: 'CONFINEMENT',
    searchKeywords: ['cocho', 'leitura', 'noturna', 'sobra', 'incidente', 'clima'],
    activePrefixes: ['/cocho'],
  },
  {
    key: 'sanitario',
    label: 'Sanitário',
    route: appRoutes.sanitario.list,
    icon: VaccinesOutlinedIcon,
    section: 'operation',
    order: 4,
    module: 'sanitary',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['sanitario', 'vacina', 'tratamento', 'vermifugo', 'protocolo'],
    activePrefixes: [appRoutes.sanitario.list],
  },

  // ── Nutrição e Produção ──────────────────────────────────────────────────
  {
    key: 'nutricao',
    label: 'Nutrição',
    route: appRoutes.nutricao.list,
    icon: RestaurantOutlinedIcon,
    section: 'nutrition',
    order: 1,
    module: 'nutrition',
    role: 'view',
    farmRequired: true,
    // Nutrição vale para todas as licenças: o pasto suplementado (sal,
    // proteico, TIP) tem rotina diária de nutrição (auditoria §5.5).
    licenseScope: 'SHARED',
    searchKeywords: ['cocho', 'ajuste diario', 'previsao', 'cms'],
    activePrefixes: [appRoutes.nutricao.list],
  },
  {
    key: 'automacao',
    label: 'Automação de Vagão',
    route: appRoutes.automacao.list,
    // Este item representa o fluxo automatizado de transporte da mistura;
    // manufatura fica reservada ao módulo de Produção.
    icon: ConveyorBeltIcon,
    section: 'nutrition',
    order: 2,
    module: 'automation',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    // Feature adicional da licença: instalação sem ela não vê nem o item.
    licenseFeature: 'automation',
    searchKeywords: ['vagao', 'misturador', 'carga', 'balanca', 'tmr'],
    activePrefixes: [appRoutes.automacao.list],
  },
  {
    key: 'ingredientes',
    label: 'Ingredientes',
    route: appRoutes.ingredientes.list,
    icon: ScienceOutlinedIcon,
    section: 'nutrition',
    order: 3,
    module: 'item',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['insumos', 'materia-prima', 'itens'],
    activePrefixes: [appRoutes.ingredientes.list],
  },
  {
    key: 'formulas',
    label: 'Fórmulas',
    route: appRoutes.formulas.list,
    icon: MenuBookOutlinedIcon,
    section: 'nutrition',
    order: 4,
    module: 'diet',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['dieta', 'receita', 'formula'],
    activePrefixes: [appRoutes.formulas.list],
  },
  {
    key: 'programas',
    label: 'Programas',
    route: appRoutes.programas.list,
    icon: HubOutlinedIcon,
    section: 'nutrition',
    order: 5,
    module: 'program',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['planejamento', 'etapas', 'cms'],
    activePrefixes: [appRoutes.programas.list],
  },
  {
    key: 'curvas-de-ganho',
    label: 'Curvas de Ganho',
    route: appRoutes.curvasDeGanho.list,
    icon: ShowChartOutlinedIcon,
    section: 'nutrition',
    order: 6,
    module: 'gainCurve',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['ganho', 'curva', 'crescimento'],
    activePrefixes: [appRoutes.curvasDeGanho.list],
  },
  {
    key: 'producao',
    label: 'Produção',
    route: appRoutes.producao.list,
    icon: PrecisionManufacturingOutlinedIcon,
    section: 'nutrition',
    order: 7,
    module: 'production',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['fabricacao', 'mistura', 'premix'],
    activePrefixes: [appRoutes.producao.list],
  },
  {
    key: 'estoque',
    label: 'Estoque',
    route: appRoutes.estoque,
    icon: WarehouseOutlinedIcon,
    section: 'nutrition',
    order: 8,
    module: 'inventory',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['entrada', 'saida', 'saldo', 'inventario'],
  },

  // ── Comercial ────────────────────────────────────────────────────────────
  {
    key: 'contratos',
    label: 'Contrato',
    route: appRoutes.contratos.list,
    icon: ShoppingCartOutlinedIcon,
    section: 'commercial',
    order: 1,
    module: 'contract',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['contrato', 'contratos', 'compra', 'compras', 'aquisicao', 'fornecedor', 'recebimento', 'gta'],
    activePrefixes: [appRoutes.contratos.list],
  },
  {
    key: 'financas',
    label: 'Vendas',
    route: appRoutes.financas.list,
    icon: AttachMoneyOutlinedIcon,
    section: 'commercial',
    order: 2,
    module: 'finance',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'receita', 'abate'],
    activePrefixes: [appRoutes.financas.list],
  },
  {
    key: 'transferencias',
    label: 'Transferências',
    route: appRoutes.transferencias.list,
    icon: SwapHorizOutlinedIcon,
    section: 'commercial',
    order: 3,
    module: 'transfer',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['transferencia', 'transferência', 'entre fazendas', 'transito', 'trânsito', 'recebimento', 'movimentacao'],
    activePrefixes: [appRoutes.transferencias.list],
  },
  {
    key: 'financas-detalhe',
    label: 'Financeiro',
    route: appRoutes.financasDetalhe,
    icon: AccountBalanceWalletOutlinedIcon,
    section: 'commercial',
    order: 4,
    module: 'finance',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['financeiro', 'dre', 'resultado', 'custos', 'margem'],
    activePrefixes: [appRoutes.financasDetalhe],
  },
  {
    key: 'compradores',
    label: 'Compradores',
    route: appRoutes.compradores.list,
    icon: StorefrontOutlinedIcon,
    section: 'commercial',
    order: 5,
    module: 'buyer',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['clientes', 'compras'],
    activePrefixes: [appRoutes.compradores.list],
  },
  {
    key: 'fornecedores',
    label: 'Fornecedores',
    route: appRoutes.fornecedores.list,
    icon: LocalShippingOutlinedIcon,
    section: 'commercial',
    order: 6,
    module: 'supplier',
    role: 'view',
    farmRequired: true,
    licenseScope: 'SHARED',
    searchKeywords: ['parceiros', 'suprimentos', 'insumos'],
    activePrefixes: [appRoutes.fornecedores.list],
  },

  // ── Administração ────────────────────────────────────────────────────────
  {
    key: 'fazendas',
    label: 'Fazendas',
    route: appRoutes.fazendas.list,
    icon: HomeWorkOutlinedIcon,
    section: 'admin',
    order: 1,
    module: 'farm',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['unidades', 'propriedades'],
    activePrefixes: [appRoutes.fazendas.list],
  },
  {
    key: 'usuarios',
    label: 'Usuários',
    route: appRoutes.usuarios.list,
    icon: PeopleOutlineOutlinedIcon,
    section: 'admin',
    order: 2,
    module: 'user',
    role: 'view',
    licenseScope: 'SHARED',
    searchKeywords: ['acessos', 'permissoes', 'equipe'],
    activePrefixes: [appRoutes.usuarios.list],
  },
  {
    key: 'automacaoPerfis',
    label: 'Perfis de balança',
    route: appRoutes.automacao.perfisTecnicos,
    icon: ScaleOutlinedIcon,
    section: 'admin',
    order: 4,
    // Só admin: publicar ou homologar perfil decide qual peso vira consumo
    // na Nutrição. Não é tela de gestor de fazenda.
    accessLevels: ['admin'],
    licenseScope: 'SHARED',
    licenseFeature: 'automation',
    searchKeywords: ['perfil', 'balanca', 'indicador', 'homologacao'],
    activePrefixes: [appRoutes.automacao.perfisTecnicos],
  },
  {
    key: 'licencas',
    label: 'Licencas',
    route: appRoutes.licencas,
    icon: AdminPanelSettingsOutlinedIcon,
    section: 'admin',
    order: 3,
    accessLevels: ['admin'],
    licenseScope: 'SHARED',
    searchKeywords: ['licencas', 'licenca', 'plano', 'modo'],
    activePrefixes: [appRoutes.licencas],
  },
];
